import React, { createContext, useContext, useState, useEffect } from 'react';
import translations from '@/data/translations.json';

// Country to language mapping
const countryLanguageMap: Record<string, 'en' | 'ro' | 'es'> = {
  RO: 'ro',    // Romania
  MD: 'ro',    // Moldova (Romanian)
  ES: 'es',    // Spain
  AR: 'es',    // Argentina
  MX: 'es',    // Mexico
  CO: 'es',    // Colombia
  // Add more Spanish-speaking countries as needed
  US: 'en',    // United States
  GB: 'en',    // United Kingdom
  CA: 'en',    // Canada
  AU: 'en',    // Australia
  // Add more English-speaking countries as needed
};

type Language = 'en' | 'ro' | 'es';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => any;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('en');

  useEffect(() => {
    // First check for saved language preference
    const savedLanguage = localStorage.getItem('language') as Language;
    if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'ro' || savedLanguage === 'es')) {
      setLanguageState(savedLanguage);
      return;
    }

    // If no saved preference, detect language from country
    const detectLanguageFromCountry = async () => {
      try {
        // Use ipapi.co to get country code (free tier available)
        const response = await fetch('https://ipapi.co/country/');
        const countryCode = await response.text();
        
        console.log('Detected country code:', countryCode);
        
        // Get language from country code or default to English
        const detectedLanguage = countryLanguageMap[countryCode] || 'en';
        setLanguageState(detectedLanguage);
        localStorage.setItem('language', detectedLanguage);
      } catch (error) {
        console.error('Error detecting country:', error);
        // Fallback to English if detection fails
        setLanguageState('en');
      }
    };

    detectLanguageFromCountry();
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key: string): any => {
    const keys = key.split('.');
    let value: any = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English if key not found in current language
        value = translations.en;
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey];
          } else {
            return key; // Return key if not found in any language
          }
        }
        break;
      }
    }
    
    // Return the actual value (string, object, array, etc.) or the key if not found
    return value !== undefined ? value : key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};